import React, { useState } from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import api from '../../api/api';
import { RebateReportResult, RebateReportResultItem } from '../../model/RebateReportResult';

const Style = styled.div`
    margin-top: 1rem;
    max-width: 600px;

    h3 {
        margin-bottom: 0.5rem;
    }
    .error {
        color: red;
    }
    
    .rebate-table {
        margin-bottom: 1.5rem;
    }
    .rebate-table .number {
        text-align: right;
    }
`;

interface IProps {
    userId?: string;
}

const UserRebateReport: React.FC<IProps> = ({ userId }) => {
    const [report, setReport] = useState<RebateReportResult | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    useAsyncEffect(async (isMounted) => {
        try {
            const result = userId
                ? await api().getRebateReport(userId)
                : await api().getMyRebateReport();
            if (isMounted()) {
                setReport(result);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    }, [userId]);

    if (loading) return <Style>Loading rebate report...</Style>;
    if (!report) return null;
    if (report.errorMessage) return <Style><div className="error">{report.errorMessage}</div></Style>;
    if (!report.items.length) return <Style><em>No paid invoices found for {report.quarterStart} - {report.quarterEnd}.</em></Style>;

    const totalRebate = report.items.reduce((sum, item) => sum + item.rebateAmount, 0);

    return (
        <Style>
            <h3>Rebate Report ({report.quarterStart} - {report.quarterEnd}) — Total: ${totalRebate.toFixed(2)}</h3>
            <table className="table rebate-table">
                <thead>
                    <tr>
                        <th>Ticker</th>
                        <th>Invoice #</th>
                        <th>Paid On</th>
                        <th className="number">Invoice Total</th>
                        <th className="number">Rebate Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {report.items.map((item: RebateReportResultItem, i: number) =>
                        item.invoices.map((inv, j) => (
                            <tr key={`${i}-${j}`}>
                                <td>{item.combinedTicker}</td>
                                <td>{inv.invoiceNumber}</td>
                                <td>{moment(inv.fullyPaidOnDate).format('D-MMM-YY')}</td>
                                <td className="number">${inv.subTotal.toFixed(2)}</td>
                                <td className="number">${item.rebateAmount.toFixed(2)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </Style>
    );
};

export default UserRebateReport;
